/**
 * Placement Calculator
 *
 * Core logic for calculating design placement within a print area.
 * Fabric.js-independent - works in pure PrintArea-Space coordinates.
 *
 * Used by:
 * - Backend: onboarding.service.ts buildCanvasData()
 * - Frontend: Wizard preview, canvas editor
 */

import { containScale } from '../math/contain.js';
import type {
  PlacementPreset,
  PlacementContext,
  PlacementResult,
  Unit,
} from './types.js';

/**
 * Resolve a Unit value to pixels in PrintArea-Space
 *
 * @param unit - The unit to resolve
 * @param ctx - Placement context with dimensions and pxPerMm
 * @param axis - Which axis ('x' for width-based, 'y' for height-based)
 * @returns Value in pixels
 */
export function resolveUnit(
  unit: Unit,
  ctx: PlacementContext,
  axis: 'x' | 'y'
): number {
  const size = axis === 'x' ? ctx.widthPx : ctx.heightPx;

  switch (unit.type) {
    case 'relative':
      return size * unit.value;
    case 'mm':
      return unit.value * ctx.pxPerMm;
    case 'px':
      return unit.value;
  }
}

/**
 * Calculate design placement based on preset and asset dimensions
 *
 * The calculator works entirely in PrintArea-Space:
 * - (0, 0) = top-left of PrintArea
 * - Output left/top = CENTER position of the design
 *
 * @param preset - The placement preset to use
 * @param assetWidth - Original asset width in pixels
 * @param assetHeight - Original asset height in pixels
 * @param ctx - Placement context (PrintArea dimensions + pxPerMm)
 * @returns Placement result with position, scale, and rotation
 *
 * @example
 * ```typescript
 * const result = calculatePlacement(
 *   PRESETS.front_center_large,
 *   2000, // asset width
 *   1000, // asset height
 *   { widthPx: 500, heightPx: 600, pxPerMm: 1.5 }
 * );
 * // result.left = 250 (center of PrintArea)
 * // result.top = 270 (45% from top)
 * // result.scale = 0.2125 (425px / 2000px)
 * ```
 */
export function calculatePlacement(
  preset: PlacementPreset,
  assetWidth: number,
  assetHeight: number,
  ctx: PlacementContext
): PlacementResult {
  // ─────────────────────────────────────────────────────────────────────────
  // 1. GUARDS - Handle invalid input
  // ─────────────────────────────────────────────────────────────────────────
  const assetW = Math.max(assetWidth, 1);
  const assetH = Math.max(assetHeight, 1);

  // ─────────────────────────────────────────────────────────────────────────
  // 2. CONTAIN BOX - Resolve to pixels
  // ─────────────────────────────────────────────────────────────────────────
  const maxW = resolveUnit(preset.maxWidth, ctx, 'x');
  const maxH = resolveUnit(preset.maxHeight, ctx, 'y');

  // ─────────────────────────────────────────────────────────────────────────
  // 3. SCALE - Contain with optional maxScale limit
  // ─────────────────────────────────────────────────────────────────────────
  let scale = containScale(assetW, assetH, maxW, maxH);

  // Apply maxScale limit (prevents excessive upscaling)
  if (preset.maxScale !== undefined && preset.maxScale > 0) {
    scale = Math.min(scale, preset.maxScale);
  }

  const scaledWidth = assetW * scale;
  const scaledHeight = assetH * scale;

  // ─────────────────────────────────────────────────────────────────────────
  // 4. POSITION - Resolve CENTER position
  // ─────────────────────────────────────────────────────────────────────────
  let left = resolveUnit(preset.positionX, ctx, 'x');
  let top = resolveUnit(preset.positionY, ctx, 'y');

  // ─────────────────────────────────────────────────────────────────────────
  // 5. CLAMPING - Keep design within PrintArea bounds
  // ─────────────────────────────────────────────────────────────────────────
  if (preset.overflow !== 'allow') {
    // Design uses center origin, so clamp to keep edges within bounds
    const halfW = scaledWidth / 2;
    const halfH = scaledHeight / 2;

    // Clamp X: center must be at least halfW from edges
    left = Math.max(halfW, Math.min(ctx.widthPx - halfW, left));

    // Clamp Y: center must be at least halfH from edges
    top = Math.max(halfH, Math.min(ctx.heightPx - halfH, top));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 6. RESULT
  // ─────────────────────────────────────────────────────────────────────────
  return {
    left, // Center X in PrintArea-Space
    top, // Center Y in PrintArea-Space
    scale,
    rotationDeg: preset.rotationDeg ?? 0,
    scaledWidth,
    scaledHeight,
  };
}

/**
 * Calculate placement for a design that needs to be positioned
 * at "horizontal center, X px from top"
 *
 * Convenience function for common "centered with top offset" pattern.
 *
 * @param topOffsetPx - Pixels from top of PrintArea to top edge of design
 * @param maxWidthPercent - Maximum width as percentage (0-1)
 * @param assetWidth - Original asset width
 * @param assetHeight - Original asset height
 * @param ctx - Placement context
 * @returns Placement result
 */
export function calculateCenteredTopPlacement(
  topOffsetPx: number,
  maxWidthPercent: number,
  assetWidth: number,
  assetHeight: number,
  ctx: PlacementContext
): PlacementResult {
  // Calculate scale to fit within max width (and max height = 90%)
  const maxW = ctx.widthPx * maxWidthPercent;
  const maxH = ctx.heightPx * 0.9;
  const scale = containScale(assetWidth, assetHeight, maxW, maxH);

  const scaledWidth = assetWidth * scale;
  const scaledHeight = assetHeight * scale;

  // Center position: center X, and top edge at topOffsetPx
  const left = ctx.widthPx / 2;
  const top = topOffsetPx + scaledHeight / 2; // Convert top-edge to center

  return {
    left,
    top,
    scale,
    rotationDeg: 0,
    scaledWidth,
    scaledHeight,
  };
}
